import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import DataTable from '../components/Tables/DataTable';
import ExpenseForm from '../components/Forms/ExpenseForm';
import { Plus, TrendingDown, AlertTriangle, CheckCircle2, RefreshCw, Flame, Zap, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';

const CATEGORY_COLORS: Record<string, string> = {
    'Hosting':  'bg-blue-500',
    'Software': 'bg-purple-500',
    'Salary':   'bg-emerald-500',
    'Tools':    'bg-orange-500',
    'Other':    'bg-gray-400',
};

const Expenses = () => {
    const { currency } = useAuth();
    const [expenses, setExpenses] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await api.get('/expenses');
            setExpenses(res.data);
        } catch (err) {
            console.error('Error fetching expenses', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const stats = useMemo(() => {
        const total = expenses.reduce((s, e) => s + (e.amount || 0), 0);
        // Revenue = expenses assigned to a client (client will pay)
        const revenueExpenses = expenses.filter(e => e.customerId);
        const revenue = revenueExpenses.reduce((s, e) => s + (e.amount || 0), 0);
        const revenueCount = revenueExpenses.length;
        // Internal = expenses NOT assigned to a client (company's own cost)
        const internalExpenses = expenses.filter(e => !e.customerId);
        const internalCost = internalExpenses.reduce((s, e) => s + (e.amount || 0), 0);
        const internalCount = internalExpenses.length;
        // Billed/unbilled still useful for status tracking
        const billed = expenses.filter(e => e.status === 'Billed').reduce((s, e) => s + (e.amount || 0), 0);
        const unbilledCount = expenses.filter(e => e.status !== 'Billed').length;
        const revenuePct = total > 0 ? Math.round((revenue / total) * 100) : 0;

        const now = new Date();
        const in30 = new Date(now.getTime() + 30 * 86400000);
        const subs = expenses.filter(e => (e.category === 'Software' || e.category === 'Hosting') && e.endDate);
        const overdueSubs = subs.filter(e => new Date(e.endDate) < now);
        const dueSoonSubs = subs.filter(e => { const d = new Date(e.endDate); return d >= now && d <= in30; });

        // Category breakdown
        const catMap: Record<string, number> = {};
        expenses.forEach(e => {
            const cat = e.category || 'Other';
            catMap[cat] = (catMap[cat] || 0) + (e.amount || 0);
        });
        const categories = Object.entries(catMap)
            .sort((a, b) => b[1] - a[1])
            .map(([name, amount]) => ({ name, amount, pct: total > 0 ? Math.round((amount / total) * 100) : 0 }));

        return { total, billed, revenue, revenueCount, internalCost, internalCount, unbilledCount, revenuePct, overdueSubs, dueSoonSubs, categories };
    }, [expenses]);

    const bannerColor = stats.overdueSubs.length > 0
        ? 'from-red-500 to-orange-500'
        : stats.unbilledCount > 0
            ? 'from-amber-500 to-yellow-500'
            : 'from-emerald-500 to-teal-500';

    const BannerIcon = stats.overdueSubs.length > 0 ? Flame : stats.unbilledCount > 0 ? Zap : ShieldCheck;

    const bannerMsg = stats.overdueSubs.length > 0
        ? `${stats.overdueSubs.length} subscription${stats.overdueSubs.length > 1 ? 's' : ''} expired — renew now!`
        : stats.unbilledCount > 0
            ? `${stats.unbilledCount} expense${stats.unbilledCount > 1 ? 's' : ''} still unbilled — follow up!`
            : expenses.length === 0
                ? 'No expenses yet — start tracking costs!'
                : 'All client expenses billed — great work!';

    const handleCreate = () => { setEditingExpense(null); setShowForm(true); };
    const handleEdit = (expense: any) => { setEditingExpense(expense); setShowForm(true); };
    const handleDelete = async (expense: any) => {
        if (window.confirm('Are you sure you want to delete this expense?')) {
            try {
                await api.delete(`/expenses/${expense.id}`);
                fetchData();
            } catch (err) {
                console.error('Error deleting expense', err);
            }
        }
    };

    const columns = [
        {
            header: 'Date',
            accessor: (item: any) => {
                const isSub = item.category === 'Software' || item.category === 'Hosting';
                if (isSub && item.startDate) return '-';
                return new Date(item.date).toLocaleDateString();
            }
        },
        { header: 'Name', accessor: (item: any) => item.name || '-' },
        { header: 'Category', accessor: 'category' },
        {
            header: 'End Date',
            accessor: (item: any) => (item.category === 'Software' || item.category === 'Hosting') && item.endDate
                ? new Date(item.endDate).toLocaleDateString()
                : '-'
        },
        { header: 'Amount', accessor: (item: any) => formatCurrency(item.amount, currency) },
        {
            header: 'Status',
            accessor: (item: any) => (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.status === 'Billed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {item.status || 'Unbilled'}
                </span>
            )
        },
    ];

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-800">Expenses</h1>
                <button
                    onClick={handleCreate}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition shadow-sm"
                >
                    <Plus size={18} />
                    Record Expense
                </button>
            </div>

            {/* Motivational Banner */}
            <div className={`relative bg-gradient-to-r ${bannerColor} rounded-xl p-5 text-white overflow-hidden`}>
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
                <div className="relative flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <BannerIcon size={16} className="text-white/90" />
                            <p className="text-white/90 text-sm font-medium">{bannerMsg}</p>
                        </div>
                        <p className="text-2xl font-bold">{formatCurrency(stats.total, currency)} <span className="text-base font-normal text-white/80">total spend</span></p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-white/80">
                            {stats.revenueCount > 0 && <span className="text-green-200 font-medium">{stats.revenueCount} client revenue</span>}
                            {stats.unbilledCount > 0 && <span className="text-yellow-200 font-medium">{stats.unbilledCount} unbilled</span>}
                            {stats.dueSoonSubs.length > 0 && <span className="flex items-center gap-1 text-orange-200"><RefreshCw size={12} /> {stats.dueSoonSubs.length} renewing soon</span>}
                        </div>
                    </div>
                    {/* Revenue % ring */}
                    <div className="flex flex-col items-center">
                        <div className="relative w-16 h-16">
                            <svg className="w-16 h-16 -rotate-90" viewBox="0 0 60 60">
                                <circle cx="30" cy="30" r="24" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="6" />
                                <circle cx="30" cy="30" r="24" fill="none" stroke="white" strokeWidth="6"
                                    strokeDasharray={`${2 * Math.PI * 24}`}
                                    strokeDashoffset={`${2 * Math.PI * 24 * (1 - stats.revenuePct / 100)}`}
                                    strokeLinecap="round"
                                    style={{ transition: 'stroke-dashoffset 0.6s ease' }}
                                />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center text-white font-bold text-sm">{stats.revenuePct}%</div>
                        </div>
                        <p className="text-xs text-white/70 mt-1">Revenue</p>
                    </div>
                </div>
                {/* Progress bar */}
                <div className="relative mt-3 h-1.5 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-white rounded-full transition-all duration-700" style={{ width: `${stats.revenuePct}%` }} />
                </div>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Total */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                            <TrendingDown size={16} className="text-blue-600" />
                        </div>
                        <span className="text-xs font-medium text-gray-500">Total Spend</span>
                    </div>
                    <p className="text-xl font-bold text-gray-800">{formatCurrency(stats.total, currency)}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{expenses.length} expenses</p>
                </div>

                {/* Revenue (client expenses) */}
                <div className="bg-white rounded-xl border border-emerald-100 p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                            <CheckCircle2 size={16} className="text-emerald-600" />
                        </div>
                        <span className="text-xs font-medium text-gray-500">Revenue</span>
                    </div>
                    <p className="text-xl font-bold text-emerald-700">{formatCurrency(stats.revenue, currency)}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{stats.revenueCount} client expense{stats.revenueCount !== 1 ? 's' : ''}</p>
                </div>

                {/* Internal Cost (no client) */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${stats.internalCount > 0 ? 'bg-amber-50' : 'bg-gray-50'}`}>
                            <AlertTriangle size={16} className={stats.internalCount > 0 ? 'text-amber-500' : 'text-gray-400'} />
                        </div>
                        <span className="text-xs font-medium text-gray-500">Internal Cost</span>
                    </div>
                    <p className={`text-xl font-bold ${stats.internalCount > 0 ? 'text-amber-600' : 'text-gray-400'}`}>{formatCurrency(stats.internalCost, currency)}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{stats.internalCount} expense{stats.internalCount !== 1 ? 's' : ''}</p>
                </div>

                {/* Subscriptions */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${stats.overdueSubs.length > 0 ? 'bg-red-50' : 'bg-purple-50'}`}>
                            <RefreshCw size={16} className={stats.overdueSubs.length > 0 ? 'text-red-500' : 'text-purple-500'} />
                        </div>
                        <span className="text-xs font-medium text-gray-500">Subscriptions</span>
                    </div>
                    <p className={`text-xl font-bold ${stats.overdueSubs.length > 0 ? 'text-red-600' : 'text-gray-800'}`}>
                        {stats.overdueSubs.length > 0 ? `${stats.overdueSubs.length} expired` : `${stats.dueSoonSubs.length} renewing`}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">in next 30 days</p>
                </div>
            </div>

            {/* Category breakdown + table side by side */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Category breakdown */}
                {stats.categories.length > 0 && (
                    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm md:col-span-1">
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">By Category</h3>
                        <div className="space-y-3">
                            {stats.categories.map((cat, i) => (
                                <div key={cat.name}>
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs font-medium text-gray-600">{cat.name}</span>
                                        <span className="text-xs text-gray-500">{cat.pct}%</span>
                                    </div>
                                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full rounded-full transition-all duration-700 ${CATEGORY_COLORS[cat.name] || 'bg-gray-400'}`}
                                            style={{ width: `${cat.pct}%` }}
                                        />
                                    </div>
                                    <p className="text-xs text-gray-400 mt-0.5">{formatCurrency(cat.amount, currency)}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Table */}
                <div className={stats.categories.length > 0 ? 'md:col-span-3' : 'md:col-span-4'}>
                    <DataTable
                        data={expenses}
                        columns={columns}
                        total={expenses.length}
                        page={1}
                        limit={100}
                        onPageChange={() => { }}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        loading={loading}
                    />
                </div>
            </div>

            {showForm && (
                <ExpenseForm
                    onClose={() => setShowForm(false)}
                    onSuccess={() => { setShowForm(false); fetchData(); }}
                    expense={editingExpense}
                />
            )}
        </div>
    );
};

export default Expenses;
